import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { BrandLogo } from "@/components/BrandLogo";
import { CheckCircle2, XCircle, Clock } from "lucide-react";

const fmt = (cur, n) => new Intl.NumberFormat("en-US", { style: "currency", currency: cur || "USD" }).format(Number(n || 0));

export default function AgencyPayResult({ mode }) {
  const [state, setState] = useState(mode === "success" ? "checking" : "cancel");
  const [data, setData] = useState(null);
  const sessionId = new URLSearchParams(window.location.search).get("session_id");

  useEffect(() => {
    if (mode !== "success" || !sessionId) return undefined;
    let tries = 0;
    let timer = null;
    const poll = async () => {
      try {
        const { data } = await api.get(`/agency/pay/status/${sessionId}`);
        setData(data);
        if (data.payment_status === "paid") { setState("paid"); return; }
      } catch {}
      tries += 1;
      if (tries < 8) timer = setTimeout(poll, 2500);
      else setState("pending");
    };
    poll();
    return () => timer && clearTimeout(timer);
  }, [mode, sessionId]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-5 py-12">
      <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-sm" data-testid="agency-pay-result">
        <div className="mb-6 flex items-center justify-center gap-2.5">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-900 p-1"><BrandLogo className="h-full w-auto" /></div>
        </div>
        {state === "checking" && (<><Clock className="mx-auto h-12 w-12 animate-pulse text-sky-600" /><h1 className="mt-4 font-display text-xl font-bold text-slate-900">Confirming your payment…</h1><p className="mt-2 text-sm text-slate-600">Please wait while we confirm your payment with our processor.</p></>)}
        {state === "paid" && (<><CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" data-testid="agency-pay-success-icon" /><h1 className="mt-4 font-display text-xl font-bold text-slate-900">Payment received</h1><p className="mt-2 text-sm text-slate-600">Thank you. We've received your first payment{data ? ` of ${fmt(data.currency, data.amount)}` : ""} for {data?.agency_name}. Your ARPS activation invitation will be sent to your email shortly.</p></>)}
        {state === "pending" && (<><Clock className="mx-auto h-12 w-12 text-amber-500" /><h1 className="mt-4 font-display text-xl font-bold text-slate-900">Payment processing</h1><p className="mt-2 text-sm text-slate-600">Your payment is being processed. You'll receive your activation invitation by email once it's confirmed.</p></>)}
        {state === "cancel" && (<><XCircle className="mx-auto h-12 w-12 text-slate-400" /><h1 className="mt-4 font-display text-xl font-bold text-slate-900">Payment canceled</h1><p className="mt-2 text-sm text-slate-600">Your payment was not completed. You can return to your payment link to try again — no invitation is sent until your first payment is confirmed.</p></>)}
      </div>
    </div>
  );
}
